import type { Infer, Struct } from 'superstruct';
import {
	array,
	assign,
	boolean,
	coerce,
	defaulted,
	dynamic,
	never,
	nonempty,
	number,
	object,
	optional,
	partial,
	string,
	union,
} from 'superstruct';

import {
	parsableFloat,
	parsableInteger,
	withMessage,
} from '@adflow/shared/util-superstruct';

export function isFacebookLocationsEmpty(
	locations: ViewFacebookAdSetLocations
) {
	return (
		(!locations.cities || locations.cities.length === 0) &&
		(!locations.zips || locations.zips.length === 0) &&
		(!locations.regions || locations.regions.length === 0) &&
		(!locations.countries || locations.countries.length === 0) &&
		(!locations.custom_locations || locations.custom_locations.length === 0)
	);
}

export function emptyStringIsUndefined<T, S>(
	struct: Struct<T, S>
): Struct<T, S> {
	return coerce(struct, string(), (x) => (x.length > 0 ? x : undefined));
}

export const TargetingGeoLocationCity = object({
	key: string(),
	name: optional(string()),
	type: optional(string()),
	country_code: optional(string()),
	country_name: optional(string()),
	region: optional(string()),
	region_id: optional(union([number(), string()])),
	radius: defaulted(parsableInteger(), () => 25),
	distance_unit: optional(string()),
	excluded: optional(boolean()),
});
export type TargetingGeoLocationCity = Infer<typeof TargetingGeoLocationCity>;

export const AddTargetingGeoLocationCity = assign(
	TargetingGeoLocationCity,
	object({
		key: withMessage(nonempty(string()), 'validations:Required'),
		excluded: defaulted(boolean(), () => false),
	})
);
export type AddTargetingGeoLocationCity = Infer<
	typeof AddTargetingGeoLocationCity
>;

export const TargetingGeoLocationRegion = object({
	key: string(),
	name: optional(string()),
	type: optional(string()),
	country_code: optional(string()),
	country_name: optional(string()),
	distance_unit: optional(string()),
	excluded: optional(boolean()),
});
export type TargetingGeoLocationRegion = Infer<
	typeof TargetingGeoLocationRegion
>;
export const AddTargetingGeoLocationRegion = assign(
	TargetingGeoLocationRegion,
	object({
		key: withMessage(nonempty(string()), 'validations:Required'),
		excluded: defaulted(boolean(), () => false),
	})
);
export type AddTargetingGeoLocationRegion = Infer<
	typeof AddTargetingGeoLocationRegion
>;

export const TargetingGeoLocationZip = object({
	key: string(),
	name: optional(string()),
	type: optional(string()),
	country_code: optional(string()),
	country_name: optional(string()),
	excluded: optional(boolean()),
});
export type TargetingGeoLocationZip = Infer<typeof TargetingGeoLocationZip>;

export const AddTargetingGeoLocationZip = assign(
	TargetingGeoLocationZip,
	object({
		key: withMessage(nonempty(string()), 'validations:Required'),
		excluded: defaulted(boolean(), () => false),
	})
);
export type AddTargetingGeoLocationZip = Infer<
	typeof AddTargetingGeoLocationZip
>;

export const TargetingGeoLocationCountry = object({
	key: string(),
	name: optional(string()),
	type: optional(string()),
	country_code: optional(string()),
	country_name: optional(string()),
	excluded: optional(boolean()),
});
export type TargetingGeoLocationCountry = Infer<
	typeof TargetingGeoLocationCountry
>;

export const AddTargetingGeoLocationCountry = assign(
	TargetingGeoLocationCountry,
	object({
		key: withMessage(nonempty(string()), 'validations:Required'),
		excluded: defaulted(boolean(), () => false),
	})
);
export type AddTargetingGeoLocationCountry = Infer<
	typeof AddTargetingGeoLocationCountry
>;

export const TargetingGeoLocationCustomLocation = object({
	name: optional(string()),
	radius: defaulted(parsableInteger(), () => 25),
	address_string: dynamic((_value, context) => {
		const contextValues = context.branch.at(-2);

		const latitude = Number.isNaN(contextValues.latitude)
			? undefined
			: Number(contextValues.latitude);
		const longitude = Number.isNaN(contextValues.longitude)
			? undefined
			: Number(contextValues.longitude);

		if (latitude && longitude) return optional(string());

		return string();
	}),
	distance_unit: optional(string()),
	latitude: optional(parsableFloat()),
	longitude: optional(parsableFloat()),
	excluded: optional(boolean()),
});
export type TargetingGeoLocationCustomLocation = Infer<
	typeof TargetingGeoLocationCustomLocation
>;

export const AddTargetingGeoLocationCustomLocation = assign(
	TargetingGeoLocationCustomLocation,
	object({
		address_string: dynamic((_value, context) => {
			const contextValues = context.branch.at(-2);

			const latitude = Number.isNaN(contextValues.latitude)
				? undefined
				: Number(contextValues.latitude);
			const longitude = Number.isNaN(contextValues.longitude)
				? undefined
				: Number(contextValues.longitude);

			if (latitude && longitude) return optional(string());

			return withMessage(nonempty(string()), 'validations:Required');
		}),
		latitude: dynamic((value, context) => {
			const contextValues = context.branch.at(-2);

			const latitude = Number.isNaN(value) ? undefined : Number(value);
			const longitude = Number.isNaN(contextValues.longitude)
				? undefined
				: Number(contextValues.longitude);

			if (contextValues.address_string)
				return optional(emptyStringIsUndefined(parsableFloat()));

			if (!latitude && !longitude)
				return optional(emptyStringIsUndefined(parsableFloat()));

			if (!latitude || !longitude)
				return withMessage(never(), 'validations:Location bothRequired');

			return optional(emptyStringIsUndefined(parsableFloat()));
		}),
		longitude: optional(emptyStringIsUndefined(parsableFloat())),
		excluded: defaulted(boolean(), () => false),
	})
);
export type AddTargetingGeoLocationCustomLocation = Infer<
	typeof AddTargetingGeoLocationCustomLocation
>;

export const AddFacebookAdSetLocations = object({
	cities: optional(array(AddTargetingGeoLocationCity)),
	zips: optional(array(AddTargetingGeoLocationZip)),
	regions: optional(array(AddTargetingGeoLocationRegion)),
	countries: optional(array(AddTargetingGeoLocationCountry)),
	custom_locations: optional(array(AddTargetingGeoLocationCustomLocation)),
});
export type AddFacebookAdSetLocations = Infer<typeof AddFacebookAdSetLocations>;

export const AddFacebookAdSetLocationTemplateDto = object({
	id: optional(parsableInteger()),
	avbId: string(),
	useSavedAudienceLocations: optional(boolean()),

	locations: AddFacebookAdSetLocations,
});

export const ViewFacebookAdSetLocations = object({
	cities: optional(array(TargetingGeoLocationCity)),
	zips: optional(array(TargetingGeoLocationZip)),
	regions: optional(array(TargetingGeoLocationRegion)),
	countries: optional(array(TargetingGeoLocationCountry)),
	custom_locations: optional(array(TargetingGeoLocationCustomLocation)),
});
export type ViewFacebookAdSetLocations = Infer<
	typeof ViewFacebookAdSetLocations
>;

export const ViewFacebookAdSetLocationTemplateDto = object({
	id: number(),
	locationType: string(),
	locations: ViewFacebookAdSetLocations,
	useSavedAudienceLocations: optional(boolean()),

	adAccount: optional(
		object({
			id: number(),
			name: string(),
			avbId: string(),
		})
	),
});
export type ViewFacebookAdSetLocationTemplateDto = Infer<
	typeof ViewFacebookAdSetLocationTemplateDto
>;
export type AddFacebookAdSetLocationTemplateDto = Infer<
	typeof AddFacebookAdSetLocationTemplateDto
>;
export const EditFacebookAdSetLocationTemplateDto = partial(
	AddFacebookAdSetLocationTemplateDto
);
export type EditFacebookAdSetLocationTemplateDto = Infer<
	typeof EditFacebookAdSetLocationTemplateDto
>;
